/**
 * Accessibility helper functions for the portfolio
 */

/**
 * Creates ARIA labels for interactive elements
 * @param {string} action - The action being performed
 * @param {string} target - The target of the action
 * @param {number} index - Optional index for lists
 * @returns {string} Proper ARIA label
 */
export const createAriaLabel = (action, target, index = null) => {
  const indexText = index !== null ? ` ${index + 1}` : '';
  return `${action} ${target}${indexText}`;
};

/**
 * Creates keyboard event handler for accessibility
 * @param {Function} callback - Function to call on activation
 * @param {Array} keys - Array of keys that should trigger the callback (default: ['Enter', ' '])
 * @returns {Function} Keyboard event handler
 */
export const createKeyboardHandler = (callback, keys = ['Enter', ' ']) => {
  return (event) => {
    if (keys.includes(event.key)) {
      event.preventDefault();
      callback(event);
    }
  };
};

/**
 * Creates focus management for modal/viewer components
 * @param {string} componentId - ID of the component
 * @returns {Object} Focus management utilities
 */
export const createFocusManager = (componentId) => {
  let previouslyFocused = null;

  return {
    trapFocus: () => {
      previouslyFocused = document.activeElement;
      const component = document.getElementById(componentId);
      if (component) {
        const focusableElements = component.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    },

    restoreFocus: () => {
      if (previouslyFocused) {
        previouslyFocused.focus();
        previouslyFocused = null;
      }
    },

    handleTabKey: (event) => {
      const component = document.getElementById(componentId);
      if (!component) return;

      const focusableElements = component.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },
  };
};

/**
 * Creates screen reader announcements
 * @param {string} message - Message to announce
 * @param {string} priority - Priority level ('polite' or 'assertive')
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.setAttribute('class', 'sr-only');
  announcement.textContent = message;

  document.body.appendChild(announcement);

  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Validates color contrast for accessibility
 * @param {string} foreground - Foreground color (hex)
 * @param {string} background - Background color (hex)
 * @returns {Object} Contrast information
 */
export const checkColorContrast = (foreground, background) => {
  // Simple implementation - in real app, use a proper contrast checking library
  const getLuminance = (hex) => {
    const rgb = parseInt(hex.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;

    const [rs, gs, bs] = [r, g, b].map((c) => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);

  return {
    ratio: Math.round(ratio * 100) / 100,
    passAA: ratio >= 4.5,
    passAAA: ratio >= 7,
    level: ratio >= 7 ? 'AAA' : ratio >= 4.5 ? 'AA' : 'Fail',
  };
};
